import { Option } from "effect";
import * as AsyncData from "foldkit/asyncData";
import type { Html } from "foldkit/html";
import { defineView } from "foldkit/submodel";
import { namedInteractions } from "../../app/namedInteractions";

import { imageBase } from "../../api/client";
import { i18next } from "../../i18n";
import { busyAttributes, busySpinner } from "../../widgets/busy";
import type { Shared } from "../shared";
import { Message } from "./message";
import type { Model } from "./model";

const t = (key: string, options: Record<string, unknown> = {}) =>
	i18next.t(`integrations.${key}`, options);

/** v1 `Integrations.tsx` minus the dead Gmail/Outlook/Slack sections (ledger) and the removed Telegram card. */
export const view = defineView<Model, Message, Shared>((model, _shared, h) => {
	const mcpUrl = `${imageBase()}/mcp`;
	const keysBody = (): Html => {
		if (AsyncData.isLoading(model.keys) || AsyncData.isIdle(model.keys)) {
			return h.div(
				[h.Class("text-center py-4")],
				[h.div([h.Class("loading loading-spinner loading-md")], [])],
			);
		}
		const keys = AsyncData.isSuccess(model.keys) ? model.keys.data.data : [];
		if (keys.length === 0) {
			return h.p(
				[h.Class("text-base-content/60 text-sm mt-4")],
				["No API keys yet."],
			);
		}
		return h.div(
			[h.Class("overflow-x-auto mt-4")],
			[
				h.table(
					[h.Class("table table-sm")],
					[
						h.thead(
							[],
							[
								h.tr(
									[],
									[
										h.th([], ["Description"]),
										h.th([], ["Created"]),
										h.th([], []),
									],
								),
							],
						),
						h.tbody(
							[],
							keys.map((key) =>
								h.tr(
									[],
									[
										h.td(
											[h.Class("font-mono text-xs")],
											[key.description || "—"],
										),
										h.td(
											[h.Class("text-xs")],
											[new Date(key.createdAt).toLocaleDateString()],
										),
										h.td(
											[h.Class("text-right")],
											[
												h.button(
													[
														h.Type("button"),
														h.Class("btn btn-ghost btn-xs text-error"),
														h.AriaLabel(
															`Delete key ${key.description || key.id}`,
														),
														h.OnClick(Message.ClickedDeleteKey({ id: key.id })),
													],
													["Delete"],
												),
											],
										),
									],
								),
							),
						),
					],
				),
			],
		);
	};
	const keysCard = h.div(
		[h.Class("card bg-base-100 shadow-lg")],
		[
			h.div(
				[h.Class("card-body")],
				[
					h.h3([h.Class("text-xl font-semibold")], ["API Keys"]),
					h.p(
						[h.Class("text-base-content/70")],
						[
							"Create API keys for MCP integrations with Claude Code, Cursor, and other AI tools.",
						],
					),
					...Option.match(model.revealedKey, {
						onNone: () => [],
						onSome: (key) => [
							h.div(
								[h.Class("alert alert-warning mt-4"), h.Role("status")],
								[
									h.div(
										[h.Class("w-full")],
										[
											h.p(
												[h.Class("font-semibold text-sm")],
												["Copy your API key now — it won't be shown again:"],
											),
											h.div(
												[h.Class("flex items-center gap-2 mt-2")],
												[
													h.code(
														[
															h.Class(
																"text-xs font-mono bg-base-200 px-3 py-2 rounded flex-1 break-all",
															),
														],
														[key],
													),
													h.button(
														[
															h.Type("button"),
															h.Class("btn btn-sm btn-outline"),
															h.AriaLabel("Copy API key"),
															h.OnClick(Message.ClickedCopy({ target: "key" })),
														],
														[
															Option.contains(model.copied, "key")
																? "Copied!"
																: "Copy",
														],
													),
												],
											),
										],
									),
								],
							),
						],
					}),
					h.div(
						[h.Class("flex items-end gap-3 mt-4")],
						[
							h.div(
								[h.Class("form-control flex-1")],
								[
									h.label(
										[h.Class("label"), h.For("key-description")],
										[h.span([h.Class("label-text")], ["Description"])],
									),
									h.input([
										h.Id("key-description"),
										h.Type("text"),
										h.Class("input input-bordered input-sm"),
										h.Placeholder("e.g. Claude Code"),
										h.Value(model.keyDescription),
										h.OnInput((value) =>
											Message.ChangedKeyDescription({ value }),
										),
									]),
								],
							),
							h.button(
								[
									h.Type("button"),
									h.Class("btn btn-primary btn-sm"),
									h.AriaLabel("Create Key"),
									...busyAttributes(h, model.creating),
									h.OnClick(Message.ClickedCreateKey()),
								],
								[busySpinner(h, model.creating), "Create Key"],
							),
						],
					),
					keysBody(),
					h.div(
						[h.Class("mt-6 space-y-4")],
						[
							h.h4([h.Class("font-semibold")], ["Setup Instructions"]),
							h.div(
								[h.Class("collapse collapse-arrow bg-base-200")],
								[
									h.input([
										h.Type("radio"),
										h.Name("mcp-setup"),
										h.Checked(true),
										h.AriaLabel("Claude.ai (recommended)"),
									]),
									h.div(
										[h.Class("collapse-title font-medium text-sm")],
										["Claude.ai (recommended)"],
									),
									h.div(
										[h.Class("collapse-content text-sm space-y-2")],
										[
											h.p(
												[],
												["Connect directly via OAuth — no API key needed."],
											),
											h.ol(
												[h.Class("list-decimal list-inside space-y-1 text-xs")],
												[
													h.li(
														[],
														[
															"Open ",
															h.a(
																[
																	h.Href(
																		"https://claude.ai/settings/integrations",
																	),
																	h.Target("_blank"),
																	h.DataAttribute(
																		"analytics-event",
																		namedInteractions.integrationsLink,
																	),
																	h.Rel("noopener noreferrer"),
																	h.Class("link link-primary"),
																],
																["Claude.ai Settings → Integrations"],
															),
														],
													),
													h.li(
														[],
														[
															'Click "Add Integration" and enter the server URL:',
														],
													),
												],
											),
											h.pre(
												[
													h.Class(
														"text-xs bg-base-300 px-3 py-2 rounded overflow-x-auto",
													),
												],
												[mcpUrl],
											),
											h.p(
												[h.Class("text-xs text-base-content/60")],
												[
													"You'll be redirected to sign in with your Rhysomn account to authorize access.",
												],
											),
										],
									),
								],
							),
							h.div(
								[h.Class("collapse collapse-arrow bg-base-200")],
								[
									h.input([
										h.Type("radio"),
										h.Name("mcp-setup"),
										h.AriaLabel("Claude Code / Cursor / Other MCP Clients"),
									]),
									h.div(
										[h.Class("collapse-title font-medium text-sm")],
										["Claude Code / Cursor / Other MCP Clients"],
									),
									h.div(
										[h.Class("collapse-content text-sm space-y-2")],
										[
											h.p(
												[],
												[
													"Use an API key to connect from CLI tools and editors. Create a key above, then add this to your MCP config:",
												],
											),
											h.pre(
												[
													h.Class(
														"text-xs bg-base-300 px-3 py-2 rounded overflow-x-auto whitespace-pre",
													),
												],
												[
													JSON.stringify(
														{
															mcpServers: {
																rhysomn: {
																	type: "streamable-http",
																	url: mcpUrl,
																	headers: {
																		Authorization: "Bearer YOUR_API_KEY",
																	},
																},
															},
														},
														null,
														2,
													),
												],
											),
											h.p(
												[h.Class("text-xs text-base-content/60")],
												[
													"For Claude Code, add this to .mcp.json in your project root. For Cursor, add it in Settings → MCP Servers.",
												],
											),
										],
									),
								],
							),
						],
					),
				],
			),
		],
	);
	return h.div(
		[
			h.DataAttribute("testid", "page-Integrations"),
			h.Class("max-w-6xl mx-auto p-6"),
		],
		[
			h.div(
				[h.Class("mb-8")],
				[
					h.h1([h.Class("text-3xl font-bold mb-2")], [t("title")]),
					h.p([h.Class("text-base-content/70")], [t("description")]),
				],
			),
			h.div(
				[h.Class("space-y-6")],
				[
					h.div(
						[],
						[
							h.h2([h.Class("text-xl font-semibold mb-4")], ["Developer"]),
							h.div([h.Class("grid gap-6")], [keysCard]),
						],
					),
				],
			),
		],
	);
});
